import React, { useEffect, useState } from 'react';

import { NativeResources } from '@ariksa/inventory-core/api';
import { Box, HStack, Stack } from '@chakra-ui/react';
import { each, every, isEmpty, map, orderBy, sumBy } from 'lodash';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { customTheme } from 'theme';

import { WithResourceIcon } from 'components/DataDisplay';
import formatNumber from 'components/DataDisplay/Utils/formatNumber';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import { renderNoEntitiesFoundWithIcon } from 'containers/Dashboard/utils';
import { dashboardWidgetLineStyles } from 'containers/Dashboard/utils/styles';
import { renderEmptyRows } from 'containers/Dashboard/utils/utils';
import { selectVulnerabilityOverview } from 'containers/Dashboard/VulnerabilityOverview/selectors';
import { toTitleCase } from 'utils/string';

export const VulnerabilitiesByResourceTypes: React.FC = () => {
  const { vulnerabilitiesByResourceTypes } = useSelector(
    selectVulnerabilityOverview,
  );
  const [data, setData] = useState<Record<string, any>[]>([]);
  const [noData, setNoData] = useState<boolean>(false);
  const navigate = useNavigate();
  const [total, setTotal] = useState<number>(0);

  useEffect(() => {
    let data: Record<string, any>[] = [];
    each(vulnerabilitiesByResourceTypes.data?.items, o => {
      if (!!o.vulnerability_count.critical || !!o.vulnerability_count.high)
        data.push({
          ...o,
          total:
            (o.vulnerability_count.critical ?? 0) +
            (o.vulnerability_count.high ?? 0),
          onClick: () =>
            navigate(
              '/visibility/vulnerability?show=Details&tab=' +
                (o?.agnostic_name === 'Containers'
                  ? NativeResources.ElasticContainerService
                  : o?.agnostic_name === 'Image'
                  ? NativeResources.ElasticContainerRegistry
                  : o?.agnostic_name),
            ),
        });
    });
    setData(orderBy(data, o => o.total, ['desc']));
    setNoData(every(data, o => o.total === 0));
    setTotal(sumBy(data, o => o.total));
  }, [vulnerabilitiesByResourceTypes.data, navigate]);

  const renderLegend = (category, vulnerability_count, total, onClick) => {
    const value = formatNumber(total, 1);

    return (
      <HStack
        onClick={onClick}
        cursor="pointer"
        justify="space-between"
        spacing={6}
        {...dashboardWidgetLineStyles}
        py={2.5}
      >
        <Box>
          <WithResourceIcon resourceType={category}>
            <Stack spacing={0}>
              <Box fontSize="dashboardRowHeader">{toTitleCase(category)}</Box>
              <Box
                fontSize="dashboardRowSubHeader"
                color={customTheme.colors.dashboardRowSubHeader}
              >
                {vulnerability_count.critical && 'Critical'}
                {vulnerability_count.critical &&
                  vulnerability_count.high &&
                  ' and high'}
                {!vulnerability_count.critical &&
                  vulnerability_count.high &&
                  'High'}{' '}
                severity
              </Box>
            </Stack>
          </WithResourceIcon>
        </Box>
        <Box>{value}</Box>
      </HStack>
    );
  };

  return (
    <DashboardWidget
      label="Vulnerabilities by Resource Types"
      isLoading={vulnerabilitiesByResourceTypes.isLoading}
      subHeader={formatNumber(total) + ' critical or high severity'}
      noDataMessage={renderNoEntitiesFoundWithIcon()}
      noData={isEmpty(data) || noData}
      content={
        <Stack w="full" h="full" spacing={0} justify="space-between">
          {map(data, o =>
            renderLegend(
              o?.agnostic_name,
              o?.vulnerability_count,
              o?.total,
              o?.onClick,
            ),
          )}
          {renderEmptyRows(data?.length)}
        </Stack>
      }
      styles={{ contentWrapper: { px: 0 } }}
    />
  );
};
